import React, { useState } from 'react';

// Interaction type config
const INTERACTION_TYPES = {
  call: { label: 'Call', icon: '📞', color: 'bg-blue-100 text-blue-700' },
  meeting: { label: 'Meeting', icon: '👥', color: 'bg-purple-100 text-purple-700' },
  email: { label: 'Email', icon: '✉️', color: 'bg-green-100 text-green-700' },
  note: { label: 'Note', icon: '📝', color: 'bg-gray-100 text-gray-700' },
  task: { label: 'Task', icon: '✅', color: 'bg-orange-100 text-orange-700' },
  other: { label: 'Other', icon: '💬', color: 'bg-gray-100 text-gray-600' },
};

export default function InteractionTimeline({ 
  interactions = [], 
  onAddNote, 
  isAdding = false,
  contactId,
  opportunityId,
}) {
  const [newNote, setNewNote] = useState('');
  const [interactionType, setInteractionType] = useState('note');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!newNote.trim()) return;
    onAddNote(newNote.trim(), interactionType);
    setNewNote('');
    setInteractionType('note');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getTypeConfig = (type) => INTERACTION_TYPES[type] || INTERACTION_TYPES.other;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Interactions Timeline</h3>

      {/* Add Note Form */}
      <form onSubmit={handleSubmit} className="mb-6">
        <div className="flex gap-2 mb-2">
          <select
            value={interactionType}
            onChange={(e) => setInteractionType(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-zapt-purple focus:border-zapt-purple text-sm"
          >
            {Object.entries(INTERACTION_TYPES).map(([value, config]) => (
              <option key={value} value={value}>
                {config.icon} {config.label}
              </option>
            ))}
          </select>
        </div>
        <textarea
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
          placeholder="Add a note, record a call, or log an interaction..."
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-zapt-purple focus:border-zapt-purple mb-2"
        />
        <button
          type="submit"
          disabled={isAdding || !newNote.trim()}
          className="px-4 py-2 bg-zapt-purple text-white text-sm rounded-lg hover:bg-zapt-purple/90 disabled:opacity-50"
        >
          {isAdding ? 'Adding...' : 'Add Interaction'}
        </button>
      </form>

      {/* Timeline */}
      {interactions.length === 0 ? (
        <p className="text-gray-500 text-sm">No interactions yet. Add one above to track your communications.</p>
      ) : (
        <div className="space-y-4">
          {interactions.map((interaction) => {
            const typeConfig = getTypeConfig(interaction.interactionType);
            return (
              <div key={interaction.id} className="relative pl-8 border-l-2 border-gray-200">
                <div className={`absolute -left-3 top-0 w-6 h-6 rounded-full flex items-center justify-center text-xs ${typeConfig.color}`}>
                  {typeConfig.icon}
                </div>
                <div className="pb-4">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${typeConfig.color}`}>
                      {typeConfig.label}
                    </span>
                    {interaction.subject && (
                      <span className="text-sm font-medium text-gray-900">
                        {interaction.subject}
                      </span>
                    )}
                    <span className="text-xs text-gray-500">
                      {formatDate(interaction.interactionDate)}
                    </span>
                    {interaction.durationMinutes && (
                      <span className="text-xs text-gray-500">
                        ({interaction.durationMinutes} min)
                      </span>
                    )}
                  </div>
                  <p className="text-gray-700 whitespace-pre-wrap">{interaction.content}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    by {interaction.authorEmail || 'Unknown'}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
